import { useMutation } from '@tanstack/react-query'
import { API_ROUTES } from '@/constants/routes'
import { api } from '@/lib/axios'

interface TokenExchangePayload {
    code: string
    redirect_uri: string
    code_verifier: string
}

interface TokenResponse {
    access_token: string
    refresh_token: string
    expires_in: number
    token_type: string
}

interface CanvaDesign {
    id: string
    title: string
    thumbnail?: {
        url: string
        width: number
        height: number
    }
    urls?: {
        edit_url?: string
        view_url?: string
    }
    created_at?: string
    updated_at?: string
}

interface ListDesignsResponse {
    items: CanvaDesign[]
    continuation?: string
}

interface ExportResult {
    status: string
    urls: { url: string }[]
}

export type { CanvaDesign }

export function useCanvaTokenExchange() {
    return useMutation({
        mutationFn: async (payload: TokenExchangePayload): Promise<TokenResponse> => {
            const { data } = await api.post(API_ROUTES.canvaToken, payload)
            return data
        },
    })
}

export function useCanvaRefreshToken() {
    return useMutation({
        mutationFn: async (refreshToken: string): Promise<TokenResponse> => {
            const { data } = await api.post(API_ROUTES.canvaRefresh, { refresh_token: refreshToken })
            return data
        },
    })
}

export function useCanvaListDesigns() {
    return useMutation({
        mutationFn: async (params: { canva_access_token: string; continuation?: string }): Promise<ListDesignsResponse> => {
            const { data } = await api.post(API_ROUTES.canvaDesigns, params)
            return data
        },
    })
}

export function useCanvaExportDesign() {
    return useMutation({
        mutationFn: async (params: { canva_access_token: string; design_id: string; format?: string }): Promise<ExportResult> => {
            const { data } = await api.post(API_ROUTES.canvaExport, params)
            return data
        },
    })
}

interface CanvaImportPayload {
    canva_access_token: string
    design_id: string
    title: string
    width_mm: number
    height_mm: number
    format?: string
}

interface CanvaImportResult {
    id: number
    name: string
    pages: { id: number; page_number: number; canvas_json: Record<string, unknown> }[]
    width_mm: number
    height_mm: number
}

export function useCanvaImportDesign() {
    return useMutation({
        mutationFn: async (params: CanvaImportPayload): Promise<CanvaImportResult> => {
            const { data } = await api.post(API_ROUTES.canvaImport, params)
            return data
        },
    })
}
